import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from '../select';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '../muti-select';
import { cn } from '../../cn';
import {
  type ReactNode,
  type MouseEventHandler,
  type UIEventHandler,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'lodash-es';
import { Spinner } from '../spinner';
import { SearchInput } from './SearchInput';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = { label: ReactNode; value: string; object?: any };

export type SelectCustomProps<TValue> = {
  value?: TValue | null;
  options: Option[];
  onChange?: (value: TValue, option: Option | Option[] | undefined) => void;
  placeholder?: string;
  mode?: 'single' | 'multiple';
  allowSearch?: boolean;
  className?: string;
  allowClear?: boolean;
  disabled?: boolean;
  onContentScroll?: UIEventHandler<HTMLDivElement>;
  loading?: boolean;
  onSearch?: (value: string) => void;
  onClearSearch?: () => void;
  contentClassname?: string;
  customSearch?: (
    search: string,
     
    option: Option,
  ) => void;
  extra?: ReactNode;
};

export function SelectCustom<TValue>({
  value,
  options,
  onChange,
  mode,
  allowSearch = false,
  placeholder,
  className,
  allowClear = true,
  disabled,
  onContentScroll,
  loading,
  onSearch,
  onClearSearch,
  contentClassname,
  customSearch,
  extra,
}: SelectCustomProps<TValue>) {
  const [search, setSearch] = useState('');

  const handleClear: MouseEventHandler<SVGSVGElement> = e => {
    e.stopPropagation();
    onChange?.('' as TValue, undefined);
  };

  const handleSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setSearch(searchValue);
        onSearch?.(searchValue);
      }, 300),
    [onSearch],
  );

  const filteredOptions = customSearch
    ? options.filter(option => customSearch(search, option))
    : options;

  const renderSelect = () => {
    switch (mode) {
      case 'multiple':
        return (
          <MultiSelect
            values={value as string[]}
            onValuesChange={e =>
              onChange?.(
                e as TValue,
                filteredOptions.filter(option => e.includes(option.value)),
              )
            }
          >
            <MultiSelectTrigger
              className={cn('w-full', className, {
                'cursor-not-allowed': disabled,
              })}
              disabled={disabled}
            >
              <MultiSelectValue
                placeholder={placeholder}
                clickToRemove={disabled ? false : allowClear}
              />
            </MultiSelectTrigger>
            <MultiSelectContent
              onScrollCapture={onContentScroll}
              search={false}
              className={cn('max-h-[280px]', contentClassname)}
            >
              {allowSearch ? (
                <div className="p-1">
                  <SearchInput
                    className="h-8"
                    placeholder="Search"
                    onKeyDown={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      handleSearch(e.target.value);
                    }}
                  />
                </div>
              ) : null}
              <MultiSelectGroup>
                {filteredOptions.map(option => (
                  <MultiSelectItem
                    key={option.value}
                    value={String(option.value)}
                  >
                    {option.label}
                  </MultiSelectItem>
                ))}
              </MultiSelectGroup>
              {loading ? <Spinner /> : null}
              {extra}
            </MultiSelectContent>
          </MultiSelect>
        );
      default:
        return (
          <Select
            value={(value ?? '') as string}
            onValueChange={e =>
              onChange?.(
                e as TValue,
                filteredOptions.find(option => option.value === e),
              )
            }
            onOpenChange={() => {
              onClearSearch?.();
            }}
          >
            <SelectTrigger
              className={cn('w-full', className)}
              onClear={allowClear && !disabled ? handleClear : undefined}
              disabled={disabled}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent
              onScroll={onContentScroll}
              onScrollCapture={onContentScroll}
              className={cn('max-h-[280px]', contentClassname)}
            >
              {allowSearch ? (
                <SearchInput
                  className="mb-2 h-8"
                  placeholder="Search"
                  onKeyDown={e => e.stopPropagation()}
                  onChange={e => {
                    e.stopPropagation();
                    handleSearch(e.target.value);
                  }}
                />
              ) : null}
              <SelectGroup>
                {filteredOptions.map(option => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
              {loading ? <Spinner /> : null}
              {extra}
            </SelectContent>
          </Select>
        );
    }
  };
  return renderSelect();
}
